import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, ExternalLink, X, Loader2, FileText, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { fetchAuthBlobUrl } from './ProjectWall';
import { cn } from '@/lib/utils';

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const WHEEL_ZOOM_STEP = 0.0025;

/**
 * Aperçu inline d'un fichier : PDF, image, vidéo, audio, texte.
 * Reçoit `inlineUrl` (URL /api authentifiée) et le charge en blob pour le rendre visualisable.
 * Améliorations :
 *  - Zoom molette / trackpad / pinch pour les images, avec pan quand zoomé.
 *  - Double-clic image = toggle zoom 1× ↔ 2.5×.
 *  - Contenu toujours centré, container assez grand.
 *  - Raccourcis clavier : Échap ferme, +/- zoom, 0 reset.
 */
export function AttachmentPreviewDialog({ open, onOpenChange, inlineUrl, downloadUrl, filename, kind, mime, size }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [textContent, setTextContent] = useState(null);
    const [error, setError] = useState(null);

    // Zoom & pan (utilisés uniquement pour les images)
    const [scale, setScale] = useState(1);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);
    const containerRef = useRef(null);
    const isPanningRef = useRef(false);
    const panStartRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
    const pinchRef = useRef(null);

    const displayKind = kind || (mime ? (
        mime.startsWith('image/') ? 'image'
            : mime.startsWith('video/') ? 'video'
            : mime.startsWith('audio/') ? 'audio'
            : mime === 'application/pdf' ? 'pdf'
            : mime.startsWith('text/') || mime === 'application/json' ? 'text'
            : 'file'
    ) : 'file');

    const resetZoom = () => { setScale(1); setTx(0); setTy(0); };

    useEffect(() => {
        if (!open || !inlineUrl) return;
        let alive = true;
        setError(null);
        setBlobUrl(null);
        setTextContent(null);
        resetZoom();
        (async () => {
            try {
                const url = await fetchAuthBlobUrl(inlineUrl);
                if (!alive) return;
                setBlobUrl(url);
                if (displayKind === 'text') {
                    try {
                        const r = await fetch(url);
                        const t = await r.text();
                        if (alive) setTextContent(t);
                    } catch { /* noop */ }
                }
            } catch {
                if (alive) setError('Impossible de charger l\u2019aperçu');
            }
        })();
        return () => { alive = false; };
    }, [open, inlineUrl, displayKind]);

    // Raccourcis clavier
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === 'Escape') onOpenChange(false);
            else if (displayKind === 'image') {
                if (e.key === '+' || e.key === '=') setScale((s) => Math.min(MAX_SCALE, s * 1.25));
                else if (e.key === '-' || e.key === '_') setScale((s) => {
                    const n = Math.max(MIN_SCALE, s / 1.25);
                    if (n <= 1.001) { setTx(0); setTy(0); return 1; }
                    return n;
                });
                else if (e.key === '0') resetZoom();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, displayKind, onOpenChange]);

    // Zoom molette / pinch trackpad — image uniquement
    useEffect(() => {
        const el = containerRef.current;
        if (!el || !open || displayKind !== 'image') return;
        const onWheel = (e) => {
            e.preventDefault();
            const rect = el.getBoundingClientRect();
            const cx = e.clientX - rect.left - rect.width / 2;
            const cy = e.clientY - rect.top - rect.height / 2;
            const factor = Math.exp(-e.deltaY * WHEEL_ZOOM_STEP);
            setScale((prev) => {
                const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * factor));
                if (next === prev) return prev;
                const ratio = next / prev;
                setTx((ptx) => (ptx - cx) * ratio + cx);
                setTy((pty) => (pty - cy) * ratio + cy);
                if (next <= 1.001) { setTx(0); setTy(0); return 1; }
                return next;
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [open, displayKind]);

    const doDownload = async () => {
        try {
            const url = blobUrl || await fetchAuthBlobUrl(downloadUrl);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'fichier';
            document.body.appendChild(a); a.click(); a.remove();
        } catch { /* noop */ }
    };

    const isZoomed = scale > 1.001;

    // Pan souris (image zoomée)
    const onMouseDown = (e) => {
        if (displayKind !== 'image' || !isZoomed) return;
        e.preventDefault();
        isPanningRef.current = true;
        panStartRef.current = { x: e.clientX, y: e.clientY, tx, ty };
    };
    const onMouseMove = (e) => {
        if (!isPanningRef.current) return;
        setTx(panStartRef.current.tx + (e.clientX - panStartRef.current.x));
        setTy(panStartRef.current.ty + (e.clientY - panStartRef.current.y));
    };
    const stopPan = () => { isPanningRef.current = false; };

    // Pinch tactile
    const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const onTouchStart = (e) => {
        if (displayKind !== 'image') return;
        if (e.touches.length === 2) {
            pinchRef.current = { startDist: dist(e.touches[0], e.touches[1]), startScale: scale };
        } else if (e.touches.length === 1 && isZoomed) {
            isPanningRef.current = true;
            panStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, tx, ty };
        }
    };
    const onTouchMove = (e) => {
        if (e.touches.length === 2 && pinchRef.current) {
            e.preventDefault();
            const d = dist(e.touches[0], e.touches[1]);
            const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchRef.current.startScale * (d / pinchRef.current.startDist)));
            if (next <= 1.001) { setScale(1); setTx(0); setTy(0); } else setScale(next);
        } else if (e.touches.length === 1 && isPanningRef.current) {
            setTx(panStartRef.current.tx + (e.touches[0].clientX - panStartRef.current.x));
            setTy(panStartRef.current.ty + (e.touches[0].clientY - panStartRef.current.y));
        }
    };
    const onTouchEnd = (e) => {
        if (e.touches.length < 2) pinchRef.current = null;
        if (e.touches.length === 0) isPanningRef.current = false;
    };

    const onDoubleClick = (e) => {
        if (displayKind !== 'image') return;
        e.stopPropagation();
        if (isZoomed) resetZoom(); else setScale(2.5);
    };

    const zoomBy = (factor) => {
        setScale((s) => {
            const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s * factor));
            if (next <= 1.001) { setTx(0); setTy(0); return 1; }
            return next;
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-6xl w-[95vw] p-0 gap-0 overflow-hidden" data-testid="attachment-preview-dialog">
                {/* Header custom */}
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50">
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-800 truncate">{filename || 'Aperçu'}</div>
                        <div className="text-[11px] text-slate-500">{[mime, size].filter(Boolean).join(' · ')}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        {displayKind === 'image' && blobUrl && (
                            <>
                                <Button size="icon" variant="ghost" onClick={() => zoomBy(1 / 1.25)} disabled={!isZoomed} title="Dézoomer (-)" className="h-8 w-8" data-testid="preview-zoom-out">
                                    <ZoomOut className="w-4 h-4" />
                                </Button>
                                <span className="text-[11px] font-medium text-slate-600 w-10 text-center tabular-nums" data-testid="preview-zoom-level">
                                    {Math.round(scale * 100)}%
                                </span>
                                <Button size="icon" variant="ghost" onClick={() => zoomBy(1.25)} disabled={scale >= MAX_SCALE - 0.01} title="Zoomer (+)" className="h-8 w-8" data-testid="preview-zoom-in">
                                    <ZoomIn className="w-4 h-4" />
                                </Button>
                                {isZoomed && (
                                    <Button size="icon" variant="ghost" onClick={resetZoom} title="Réinitialiser (0)" className="h-8 w-8" data-testid="preview-zoom-reset">
                                        <RotateCcw className="w-4 h-4" />
                                    </Button>
                                )}
                                <div className="w-px h-5 bg-slate-200 mx-1" />
                            </>
                        )}
                        {blobUrl && (
                            <Button size="sm" variant="ghost" onClick={() => window.open(blobUrl, '_blank')} data-testid="preview-open-new-tab" className="gap-1 text-xs">
                                <ExternalLink className="w-3.5 h-3.5" /> Nouvel onglet
                            </Button>
                        )}
                        <Button size="sm" onClick={doDownload} data-testid="preview-download" className="gap-1 text-xs">
                            <Download className="w-3.5 h-3.5" /> Télécharger
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => onOpenChange(false)} className="h-8 w-8" aria-label="Fermer">
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Body — hauteur généreuse, centrage garanti, pas de scroll horizontal parasite */}
                <div
                    ref={containerRef}
                    className={cn(
                        "bg-slate-900/95 flex items-center justify-center overflow-hidden touch-none",
                        "h-[78vh]",
                    )}
                    onMouseDown={onMouseDown}
                    onMouseMove={onMouseMove}
                    onMouseUp={stopPan}
                    onMouseLeave={stopPan}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onDoubleClick={onDoubleClick}
                    style={{
                        cursor: displayKind === 'image' && blobUrl
                            ? (isZoomed ? (isPanningRef.current ? 'grabbing' : 'grab') : 'zoom-in')
                            : 'default',
                    }}
                    data-testid="preview-body"
                >
                    {error ? (
                        <div className="text-slate-200 text-sm p-8 text-center">
                            <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            {error}
                        </div>
                    ) : !blobUrl ? (
                        <div className="text-slate-200 text-sm flex items-center gap-2 p-8">
                            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                        </div>
                    ) : displayKind === 'image' ? (
                        <img
                            src={blobUrl}
                            alt={filename}
                            className="max-w-full max-h-full object-contain select-none"
                            draggable={false}
                            style={{
                                transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
                                transformOrigin: 'center center',
                                transition: isPanningRef.current ? 'none' : 'transform 0.1s ease-out',
                                willChange: 'transform',
                            }}
                            data-testid="preview-image"
                        />
                    ) : displayKind === 'pdf' ? (
                        <iframe title={filename} src={blobUrl} className="w-full h-full bg-white" data-testid="preview-pdf-iframe" />
                    ) : displayKind === 'video' ? (
                        <video controls src={blobUrl} className="max-w-full max-h-full" autoPlay data-testid="preview-video" />
                    ) : displayKind === 'audio' ? (
                        <div className="p-8 bg-slate-800 rounded-lg">
                            <audio controls src={blobUrl} autoPlay data-testid="preview-audio" />
                        </div>
                    ) : displayKind === 'text' ? (
                        <pre className="w-full h-full overflow-auto bg-white text-slate-800 text-xs p-4 font-mono whitespace-pre-wrap" data-testid="preview-text">{textContent ?? 'Chargement…'}</pre>
                    ) : (
                        <div className="text-slate-200 text-sm p-8 text-center">
                            <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            Ce type de fichier n&apos;a pas d&apos;aperçu — téléchargez-le pour l&apos;ouvrir.
                        </div>
                    )}
                </div>

                {/* Hint zoom image */}
                {displayKind === 'image' && blobUrl && !isZoomed && (
                    <div className="px-4 py-1.5 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500 text-center" data-testid="preview-zoom-hint">
                        Molette / pincez pour zoomer · Double-clic pour agrandir · Cliquez-glissez pour déplacer
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
